// @react
import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
// @graphql
import {
  LearningSummaryQuery,
  LearningSummaryQueryVariables,
} from 'lib/graphql/graphqlTypes'
import { GET_LEARNING_SUMMARY } from 'graphql/queries/learning.query'
// @libraries
import useResizeObserver from 'use-resize-observer'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { useMediaQuery } from '@material-ui/core'
// @common
import strings from 'lib/constants/strings'
import Helper from 'utils/Helper.class'
import { RouteNamesExternal } from 'constants/routeNamesExternal'
// @components
import Box from '@bit/atd.web.box'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
import Grid from '@bit/atd.web.grid'
import TextLink from '@bit/atd.web.text-link'
import Icons from '@bit/atd.web.icons'
import colors from '@bit/atd.web.colors/dist/colors'
import Card from '__COMPONENTS_TO_MIGRATE/CardNew'
// @module components
import QueryError from 'modules/common/components/Errors/QueryError'

/**
 * 2.0.0.5 MyATD - Dashboard - Learning Activity
 * https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3579%3A14122
 */
const useStyles = makeStyles(() => ({
  textTitleEmpty: {
    color: '#2E2D31',
    fontSize: '22px',
    fontWeight: 500,
    lineHeight: '30px',
    textAlign: 'center',
    marginBottom: 24,
  },
  actionsWrapper: {
    '& > .MuiGrid-item': {
      display: 'flex',
      textAlign: 'center',
    },
  },
  action: {
    display: 'flex',
    height: 'auto',
    width: '100%',
    boxShadow: 'none',
    '& > div': {
      display: 'flex',
      height: 'auto',
      justifyContent: 'center',
      width: '100%',
      padding: 0,
    },
    '& > div a': {
      padding: '24px 24px',
    },
    '& > div a > span': {
      marginTop: 8,
      display: 'block',
    },
  },
}))

const LearningActivity = () => {
  /******************************************
   * STATE
   ******************************************/

  const [fullWidthLayout, setFullWidthLayout] = useState(false)
  const [actionFullWidthLayout, setActionFullWidthLayout] = useState(false)

  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const { ref } = useResizeObserver<HTMLDivElement>({
    onResize: ({ width }) => {
      if (width) {
        if (isMobile) {
          setFullWidthLayout(width < 540)
          setActionFullWidthLayout(width < 480)
        }
      }
    },
  })

  /******************************************
   * GRAPHQL HOOKS
   ******************************************/

  const { loading: isLoading, error: isError, data } = useQuery<
    LearningSummaryQuery,
    LearningSummaryQueryVariables
  >(GET_LEARNING_SUMMARY)

  /******************************************
   * RENDER
   ******************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  const contentCards = data?.learningSummary.data.map((item) => {
    return (
      <Card
        key={item.id}
        type={'dashboard'}
        content={{
          title: item.title,
          titleUrl: item?.url || '',
          titleTruncate: true,
        }}
        details={{
          detailList: [
            { icon: 'calendar', label: Helper.miniDateFormatter(item.date) },
          ],
        }}
        footer={{
          contentType: item.type,
        }}
      />
    )
  })

  const contentGrid = contentCards?.map((item, index) => {
    return (
      <Grid key={`content_${index}`} item xs={fullWidthLayout ? 12 : 4}>
        {item}
      </Grid>
    )
  })

  const emptyStateBox = (
    <Box>
      <div>
        <Typography className={classes.textTitleEmpty} component={'p'}>
          {strings.DASHBOARD.LEARNING_ACTIVITY.NO_ACTIVITY_MESSAGE}
        </Typography>

        <Grid
          className={classes.actionsWrapper}
          container
          spacing={2}
          alignItems={'stretch'}
        >
          <Grid item xs={actionFullWidthLayout ? 12 : 4}>
            <Box classNames={{ root: classes.action }}>
              <TextLink
                size={'large'}
                variant={'secondary'}
                href={RouteNamesExternal.ATD_EDUCATION_SEARCH}
              >
                <Icons size={32} glyph={'courses'} color={colors.brandOrange} />
                <span>{strings.DASHBOARD.LEARNING_ACTIVITY.TAKE_A_COURSE}</span>
              </TextLink>
            </Box>
          </Grid>

          <Grid item xs={actionFullWidthLayout ? 12 : 4}>
            <Box classNames={{ root: classes.action }}>
              <TextLink
                size={'large'}
                variant={'secondary'}
                href={RouteNamesExternal.ATD_CERTIFICATIONS}
              >
                <Icons
                  size={32}
                  glyph={'certifications'}
                  color={colors.brandOrange}
                />
                <span>Get a Certification</span>
              </TextLink>
            </Box>
          </Grid>

          <Grid item xs={actionFullWidthLayout ? 12 : 4}>
            <Box classNames={{ root: classes.action }}>
              <TextLink
                size={'large'}
                variant={'secondary'}
                href={RouteNamesExternal.ATD_SHOP_CONFERENCES}
              >
                <Icons
                  size={32}
                  glyph={'conferences'}
                  color={colors.brandOrange}
                />
                <span>Attend a Conference</span>
              </TextLink>
            </Box>
          </Grid>
        </Grid>
      </div>
    </Box>
  )

  const content = !isLoading ? (
    <Grid container spacing={2}>
      {contentGrid}
    </Grid>
  ) : (
    <SkeletonLoader />
  )

  return (
    <div ref={ref}>
      {isLoading || (!isLoading && contentGrid?.length) ? (
        <Box
          variant="subModule"
          header={{
            title: strings.DASHBOARD.MY_LEARNING,
          }}
          footer={{
            link: {
              url: Helper.getPathFromRouteKey('MYATD_ACTIVITYHISTORY_LEARNING'),
              label: strings.DASHBOARD.MY_ACTIVITY,
            },
          }}
        >
          {content}
        </Box>
      ) : (
        emptyStateBox
      )}
    </div>
  )
}

export default LearningActivity
